import React, { forwardRef, useMemo, useRef, useState } from 'react';
import { Minus, Plus } from '@phosphor-icons/react';
import tw, { styled, theme } from 'twin.macro';

interface EditableCounterProps
  extends Omit<React.ComponentPropsWithoutRef<'input'>, 'onChange'> {
  increaseTooltip?: string;
  decreaseTooltip?: string;
  onChange?: (value: string) => void;
}

const EditableCounter: React.FC<EditableCounterProps> = forwardRef<
  HTMLInputElement,
  EditableCounterProps
>(({ onChange: _onChange, ...props }, ref) => {
  const fallbackRef = useRef<HTMLInputElement>(null);

  const customRef = useMemo(
    () => ref && (ref as React.MutableRefObject<HTMLInputElement>),
    [ref]
  );

  const inputRef = (customRef ?? fallbackRef)!;

  const min = props.min === undefined ? -Infinity : Number(props.min);
  const max = props.max === undefined ? Infinity : Number(props.max);

  const normalizeValue = (raw: string): number | null => {
    if (raw.trim() === '') return null;
    const value = Number(raw);
    if (!Number.isFinite(value)) return null;
    return Math.min(max, Math.max(min, Math.round(value)));
  };

  const [value, setValue] = useState(
    () => normalizeValue(String(props.defaultValue ?? props.min ?? 0)) ?? 0
  );
  const lastValue = useRef(value);
  const isMinusDisabled = value <= min;
  const isPlusDisabled = value >= max;

  const commit = (nextValue: number) => {
    lastValue.current = nextValue;
    setValue(nextValue);
    _onChange?.(String(nextValue));
  };

  const step = (
    direction: 'up' | 'down',
    e: React.MouseEvent<HTMLButtonElement>
  ) => {
    e.preventDefault();
    const input = inputRef.current!;
    input.value = String(lastValue.current);
    if (direction === 'up') input.stepUp();
    else input.stepDown();
    const nextValue = normalizeValue(input.value);
    if (nextValue !== null) commit(nextValue);
  };

  const changeHandler = (e: React.ChangeEvent<HTMLInputElement>) => {
    const nextValue = normalizeValue(e.currentTarget.value);
    // Empty or incomplete numbers are allowed while typing, without saving NaN.
    if (nextValue !== null) commit(nextValue);
  };

  const blurHandler = (e: React.FocusEvent<HTMLInputElement>) => {
    const nextValue =
      normalizeValue(e.currentTarget.value) ?? lastValue.current;
    e.currentTarget.value = String(nextValue);
    commit(nextValue);
    props.onBlur?.(e);
  };

  return (
    <Wrapper>
      <Button onClick={(e) => step('down', e)} disabled={isMinusDisabled}>
        <Minus
          weight="bold"
          // title={decreaseTooltip ?? 'Decrease'}
          style={{
            color: isMinusDisabled
              ? theme`colors.gray.400`
              : theme`colors.gray.800`,
          }}
        />
      </Button>
      <Input
        {...(props as any)}
        ref={customRef ?? fallbackRef}
        type="number"
        onChange={changeHandler}
        onBlur={blurHandler}
        style={{
          MozAppearance: 'textfield', // Hide input number arrows (Firefox)
        }}
      />
      <Button onClick={(e) => step('up', e)} disabled={isPlusDisabled}>
        <Plus
          weight="bold"
          // title={increaseTooltip ?? 'Increase'}
          style={{
            color: isPlusDisabled
              ? theme`colors.gray.400`
              : theme`colors.gray.800`,
          }}
        />
      </Button>
    </Wrapper>
  );
});

const Wrapper = styled.div(tw`border border-gray-300 rounded-lg inline-flex`);

const Input = styled.input(
  tw`max-w-[5rem] p-2 border border-y-0 border-gray-300 text-lg h-7 font-medium text-center focus:outline-none
  [::-webkit-inner-spin-button]:appearance-none [::-webkit-outer-spin-button]:appearance-none` // Hide input number arrows (Chrome, Safari, Edge, Opera)
);

const Button = styled.button(
  tw`
    outline-none appearance-none bg-white border-none items-center justify-center cursor-pointer m-0 relative flex h-7 w-6
    first-of-type:(rounded-tl-md rounded-bl-md) last-of-type:(rounded-tr-md rounded-br-md) disabled:cursor-not-allowed
  `
);

export default EditableCounter;
